import {
  CheckCircleOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowRightOutlined,
} from '@mui/icons-material';
import { Alert, Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { CustomTable } from 'src/components/table/CustomTable';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { lang } from 'src/utils';

import { mapScheduleListToTableRows } from '../BulkReplaceComponentPage.helper';
import {
  IBulkReplaceLocationGroup,
  IBulkReplaceScheduleRow,
  IBulkReplaceTableRow,
} from '../BulkReplaceComponentPage.interface';
import { StyledStatus } from '../BulkReplaceComponentPage.styled';
import { useComponentData } from '../hooks/useComponentData';

interface BulkReplaceComponentReviewStepProps {
  isLoading?: boolean;
  selectedLocationGroups?: Set<number>;
  selectedLocationGroupsData?: IBulkReplaceLocationGroup[];
  selectedScheduleItems?: Set<string>;
  onSelectScheduleItem?: (
    locationGroupId: number,
    scheduleItemId: number,
  ) => void;
  rowsPerPage?: number;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

const renderStatus = (row: IBulkReplaceScheduleRow) => {
  if (!row.status) return null;

  return (
    <CustomTooltip
      width={450}
      placement="bottom"
      title={row.status.message}
      overflowLineNumber={1}>
      <StyledStatus type={row.status.type}>{row.status.message}</StyledStatus>
    </CustomTooltip>
  );
};

export const BulkReplaceComponentReviewStep = ({
  isLoading = false,
  selectedLocationGroups,
  selectedLocationGroupsData,
  selectedScheduleItems,
  onSelectScheduleItem,
  rowsPerPage = 20,
  onRowsPerPageChange,
}: BulkReplaceComponentReviewStepProps) => {
  const [reviewCurrentPage, setReviewCurrentPage] = useState(1);
  const { replacementComponent } = useComponentData();

  const selectedDataRows = mapScheduleListToTableRows(
    {
      data: selectedLocationGroupsData || [],
      totalPages: 1,
      totalCount: selectedLocationGroupsData?.length || 0,
    },
    selectedLocationGroups,
    selectedScheduleItems,
    onSelectScheduleItem,
    true,
    replacementComponent,
  );

  const selectedDataTotalCount = selectedDataRows.length;
  const selectedDataTotalPages = Math.ceil(
    selectedDataTotalCount / rowsPerPage,
  );

  const startIndex = (reviewCurrentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedSelectedRows = selectedDataRows.slice(startIndex, endIndex);

  const handleReviewPageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setReviewCurrentPage(page);
  };

  return (
    <Stack gap={4} sx={{ flex: 1 }}>
      <Box>
        <Typography variant="h6" fontWeight="400">
          {lang.reviewChanges}
        </Typography>
        <Alert severity="success" icon={<CheckCircleOutlined />}>
          {lang.bulkReplaceComponentPage.reviewChanges.replacementChangesInfo(
            selectedLocationGroupsData?.length || 0,
          )}
        </Alert>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: '50px', // prevent footer from overlapping content
        }}>
        <CustomTableWithPagination
          loading={isLoading}
          columns={[
            {
              id: 'actions',
              label: lang.locationGroup,
              width: '25px',
            },
            {
              id: 'channel_type',
              label: lang.bulkReplaceComponentPage.channelType,
              width: '10px',
              align: 'left',
            },
            {
              id: 'number_of_schedules',
              label: lang.bulkReplaceComponentPage.numberOfSchedules,
              width: '63.5px',
            },
          ]}
          rows={paginatedSelectedRows}
          totalCount={selectedDataTotalCount}
          totalPages={selectedDataTotalPages}
          page={reviewCurrentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={handleReviewPageChange}
          showTableControls
          visibleControls={[]}
          onRowsPerPageChange={onRowsPerPageChange}
          renderActions={(row, _, expanded) => (
            <Grid
              container
              item
              wrap="nowrap"
              alignItems="center"
              justifyContent="flex-start"
              spacing={1}>
              <IconButton>
                {expanded ? (
                  <KeyboardArrowDownOutlined />
                ) : (
                  <KeyboardArrowRightOutlined />
                )}
              </IconButton>
              <CustomTooltip
                title={row.location_group || ''}
                overflowWidth={200}
                placement="right">
                <Typography>{row.location_group}</Typography>
              </CustomTooltip>
            </Grid>
          )}
          renderHeadActions={(col) => (
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '14px',
              }}>
              {col.label}
            </Typography>
          )}
          renderCollapseContent={(row: IBulkReplaceTableRow) => (
            <CustomTable
              overrideHeight="auto"
              columns={row.columns}
              rows={row.rows.map((nestedRow: IBulkReplaceScheduleRow) => ({
                ...nestedRow,
                status: renderStatus(nestedRow),
              }))}
              loading={false}
              stickyHeader={false}
              tableCellHeadProps={{
                sx: {
                  backgroundColor: 'rgba(2, 90, 197, 0.04)',
                },
              }}
              tableBodyRowProps={{
                sx: {
                  backgroundColor: 'rgba(2, 90, 197, 0.04)',
                  ':hover': {
                    backgroundColor: 'rgba(2, 90, 197, 0.04)',
                  },
                },
              }}
            />
          )}
        />
      </Box>
    </Stack>
  );
};
